import { ChartInfoKind } from '@frontend/chart/types';
import { Application, Feature } from '@frontend/functionality/types';
import { TransactionTimerName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { INSIGHTS_GRID_CLASS_NAME, getInsightCardWidthClass } from '../insight-card-width';
import { InsightId } from '../insight-registry/insight-ids';
import { StalePudoTransactionsList } from '../pudo/stale-pudo-transactions-list.component';
import {
    Last2WeeksNivalizCoffinsTransactionTimerActivityHourDayHeatmapChart,
    Last2WeeksNivalizUrnsTransactionTimerActivityHourDayHeatmapChart
} from './last-2-weeks-nivaliz-transaction-timer-activity-hour-day-heatmap-chart.component';
import {
    Last6MonthsNivalizCoffinsTransactionTimerActivityWeekdayHeatmapChart,
    Last6MonthsNivalizUrnsTransactionTimerActivityWeekdayHeatmapChart
} from './last-6-months-nivaliz-transaction-timer-activity-weekday-heatmap-chart.component';
import { NivalizCompartmentUsageBySizeDonutChart } from './nivaliz-compartment-usage-by-size-donut-chart.component';
import { NivalizDailyTurnoverRateLineChart } from './nivaliz-daily-turnover-rate-line-chart.component';

export interface NivalizInsightsPageProps {
    accountId: string;
}

/** Standalone Nivaliz insights content — reusable outside Analytics. */
export const NivalizInsightsPage = ({ accountId }: NivalizInsightsPageProps) => (
    <div className={INSIGHTS_GRID_CLASS_NAME}>
        <div className={getInsightCardWidthClass(2)}>
            <StalePudoTransactionsList
                insightId={InsightId.NIVALIZ_URNS_STALE_DROP_OFFS}
                listId='insights-nivaliz-urns-stale-drop-offs-list'
                accountId={accountId}
                application={Application.NIVALIZ}
                infoKind={ChartInfoKind.STALE_DROP_OFF_TRANSACTIONS_LIST}
                timerName={TransactionTimerName.TRANSACTION_DROP_OFF_PENDING}
                feature={Feature.OUTBOUND_TRANSACTION}
                title={
                    <FormattedMessage
                        id='platform.analytics.insights.nivaliz.urns-stale-drop-offs.title'
                        description='Title for the urns stale drop-offs transaction list on the Nivaliz analytics insights tab'
                        defaultMessage='Urns: Stale drop-offs'
                    />
                }
            />
        </div>
        <div className={getInsightCardWidthClass(2)}>
            <StalePudoTransactionsList
                insightId={InsightId.NIVALIZ_URNS_STALE_PICK_UPS}
                listId='insights-nivaliz-urns-stale-pick-ups-list'
                accountId={accountId}
                application={Application.NIVALIZ}
                infoKind={ChartInfoKind.STALE_PICK_UP_TRANSACTIONS_LIST}
                timerName={TransactionTimerName.TRANSACTION_PICK_UP_PENDING}
                feature={Feature.OUTBOUND_TRANSACTION}
                title={
                    <FormattedMessage
                        id='platform.analytics.insights.nivaliz.urns-stale-pick-ups.title'
                        description='Title for the urns stale pick-ups transaction list on the Nivaliz analytics insights tab'
                        defaultMessage='Urns: Stale pick-ups'
                    />
                }
            />
        </div>
        <div className={getInsightCardWidthClass(2)}>
            <StalePudoTransactionsList
                insightId={InsightId.NIVALIZ_COFFINS_STALE_DROP_OFFS}
                listId='insights-nivaliz-coffins-stale-drop-offs-list'
                accountId={accountId}
                application={Application.NIVALIZ}
                infoKind={ChartInfoKind.STALE_DROP_OFF_TRANSACTIONS_LIST}
                timerName={TransactionTimerName.TRANSACTION_DROP_OFF_PENDING}
                feature={Feature.INBOUND_TRANSACTION}
                title={
                    <FormattedMessage
                        id='platform.analytics.insights.nivaliz.coffins-stale-drop-offs.title'
                        description='Title for the coffins stale drop-offs transaction list on the Nivaliz analytics insights tab'
                        defaultMessage='Coffins: Stale drop-offs'
                    />
                }
            />
        </div>
        <div className={getInsightCardWidthClass(2)}>
            <StalePudoTransactionsList
                insightId={InsightId.NIVALIZ_COFFINS_STALE_PICK_UPS}
                listId='insights-nivaliz-coffins-stale-pick-ups-list'
                accountId={accountId}
                application={Application.NIVALIZ}
                infoKind={ChartInfoKind.STALE_PICK_UP_TRANSACTIONS_LIST}
                timerName={TransactionTimerName.TRANSACTION_PICK_UP_PENDING}
                feature={Feature.INBOUND_TRANSACTION}
                title={
                    <FormattedMessage
                        id='platform.analytics.insights.nivaliz.coffins-stale-pick-ups.title'
                        description='Title for the coffins stale pick-ups transaction list on the Nivaliz analytics insights tab'
                        defaultMessage='Coffins: Stale pick-ups'
                    />
                }
            />
        </div>
        <NivalizDailyTurnoverRateLineChart
            accountId={accountId}
            width={3}
        />
        <NivalizCompartmentUsageBySizeDonutChart
            accountId={accountId}
            width={1}
        />
        <Last6MonthsNivalizUrnsTransactionTimerActivityWeekdayHeatmapChart
            accountId={accountId}
            width={2}
        />
        <Last6MonthsNivalizCoffinsTransactionTimerActivityWeekdayHeatmapChart
            accountId={accountId}
            width={2}
        />
        <Last2WeeksNivalizUrnsTransactionTimerActivityHourDayHeatmapChart
            accountId={accountId}
            width={2}
        />
        <Last2WeeksNivalizCoffinsTransactionTimerActivityHourDayHeatmapChart
            accountId={accountId}
            width={2}
        />
    </div>
);
